/**
 * TanStack Query Hooks for Investigations
 *
 * This module provides comprehensive hooks for managing investigations including:
 * - Fetching investigations with filters
 * - Creating investigations
 * - Enabling/Disabling investigations (with optimistic updates)
 * - Downloading reports
 * - Sending reports
 * - Retrying reports
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { retryLongReport } from "api/expressReports";
import {
  createInvestigation,
  disableInvestigation,
  downloadCreditReport,
  downloadCreditReportMetadata,
  downloadMultireport,
  enableInvestigation,
  getAllInvestigations,
  getAllOrganizationInvestigation,
  getInvestigation,
  sendCreditReport,
  sendMetadata,
  sendMultireport,
} from "api/investigation";

import { Investigation } from "js/components/ExpressReport/types";
import { PaginationTable } from "js/components/types";

import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS - Fetching data
// ============================================================================

interface UseInvestigationsParams {
  pagination: PaginationTable;
  filter?: string;
  additionalFilters?: {
    is_active?: string;
    status?: string;
    legal_type?: string;
  };
  isOrganization?: boolean;
}

/**
 * Get all investigations with pagination and filters
 * Supports both regular and organization-specific investigations
 */
export const useInvestigations = ({
  pagination,
  filter = "",
  additionalFilters = {},
  isOrganization = false,
}: UseInvestigationsParams) => {
  return useQuery({
    queryKey: isOrganization
      ? [
          ...queryKeys.organization.all,
          "investigations",
          { pagination, filter },
        ]
      : queryKeys.investigations.list({
          pagination,
          filter,
          additionalFilters,
        }),
    queryFn: async () => {
      if (isOrganization) {
        return getAllOrganizationInvestigation(pagination, filter);
      }
      return getAllInvestigations(pagination, filter, additionalFilters);
    },
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (was cacheTime in v4)
  });
};

/**
 * Get a specific investigation by ID
 */
export const useGetInvestigation = (
  investigationId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.investigations.detail(investigationId),
    queryFn: () => getInvestigation(investigationId),
    enabled: options?.enabled ?? !!investigationId,
    staleTime: 60000, // 1 minute
  });
};

// ============================================================================
// MUTATION HOOKS - Creating/Updating data
// ============================================================================

/**
 * Create a new investigation
 * Invalidates: investigations lists, organization data (credits consumed)
 */
export const useCreateInvestigation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      legal_type,
      prospectus_email,
      report_types,
    }: {
      legal_type: string;
      prospectus_email: string;
      report_types: string[];
    }) => createInvestigation(legal_type, prospectus_email, report_types),
    onSuccess: () => {
      // Invalidate investigations lists
      queryClient.invalidateQueries({ queryKey: queryKeys.investigations.all });
      // Invalidate organization data (credits consumed)
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.all });
      // Note: Toast notification is handled by the consuming component
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al crear la investigación",
      );
      toast.error(errorMessage);
    },
  });
};

/**
 * Toggle investigation status with optimistic updates
 * This implements optimistic UI updates with rollback on error
 * Supports both "activate"/"deactivate" action strings and boolean isActive
 */
export const useToggleInvestigationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      investigationId,
      action,
      isActive,
    }: {
      investigationId: string;
      action?: "activate" | "deactivate";
      isActive?: boolean;
    }) => {
      // Support both action string and boolean isActive
      const shouldActivate = action === "activate" || isActive === true;

      if (shouldActivate) {
        return enableInvestigation(investigationId);
      } else {
        return disableInvestigation(investigationId);
      }
    },
    onMutate: async ({ investigationId, action, isActive }) => {
      // Cancel any outgoing refetches for both regular and organization queries
      await queryClient.cancelQueries({
        queryKey: queryKeys.investigations.all,
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.all,
      });

      // Snapshot the previous values
      const previousInvestigations = queryClient.getQueriesData({
        queryKey: queryKeys.investigations.all,
      });
      const previousOrganizationInvestigations = queryClient.getQueriesData({
        queryKey: [...queryKeys.organization.all, "investigations"],
      });

      // Determine the new active state
      const shouldActivate = action === "activate" || isActive === true;

      // Optimistically update all investigation queries (regular)
      queryClient.setQueriesData(
        { queryKey: queryKeys.investigations.all },
        (oldData: any) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            results: oldData.results?.map((inv: Investigation) =>
              inv.id === investigationId
                ? { ...inv, is_active: shouldActivate }
                : inv,
            ),
          };
        },
      );

      // Optimistically update all organization investigation queries
      queryClient.setQueriesData(
        { queryKey: [...queryKeys.organization.all, "investigations"] },
        (oldData: any) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            results: oldData.results?.map((inv: Investigation) =>
              inv.id === investigationId
                ? { ...inv, is_active: shouldActivate }
                : inv,
            ),
          };
        },
      );

      return { previousInvestigations, previousOrganizationInvestigations };
    },
    onError: (error: any, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousInvestigations) {
        context.previousInvestigations.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousOrganizationInvestigations) {
        context.previousOrganizationInvestigations.forEach(
          ([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          },
        );
      }

      const action =
        variables.action || (variables.isActive ? "activate" : "deactivate");
      toast.error(
        getApiErrorMessage(
          error,
          `Error al ${action === "activate" ? "activar" : "desactivar"} la investigación`,
        ),
      );
    },
    onSuccess: (data, variables) => {
      const action =
        variables.action || (variables.isActive ? "activate" : "deactivate");
      toast.success(
        `Investigación ${action === "activate" ? "activada" : "desactivada"} exitosamente`,
      );
    },
    onSettled: () => {
      // Always refetch after error or success (both regular and organization queries)
      queryClient.invalidateQueries({ queryKey: queryKeys.investigations.all });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organization.all, "investigations"],
      });
    },
  });
};

/**
 * Retry a long report
 * Invalidates: investigations lists and specific investigation
 */
export const useRetryLongReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: retryLongReport,
    onMutate: async () => {
      const hide = toast.loading("Actualizando...");
      return { hide };
    },
    onError: (error: any, variables, context) => {
      if (context?.hide) toast.dismiss(context.hide);
      toast.error(
        getApiErrorMessage(
          error,
          "Algo salió mal, inténtelo de nuevo más tarde.",
        ),
      );
    },
    onSuccess: (data, variables, context) => {
      if (context?.hide) toast.dismiss(context.hide);
      toast.success("Reporte actualizado exitosamente");
      queryClient.invalidateQueries({ queryKey: queryKeys.investigations.all });
    },
  });
};

// ============================================================================
// DOWNLOAD HOOKS - Downloading reports
// ============================================================================

/**
 * Helper function to trigger file download
 */
const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Download investigation multireport
 */
export const useDownloadInvestigationMultireport = () => {
  return useMutation({
    mutationFn: async (investigationId: string) => {
      const response = await downloadMultireport(investigationId);
      return response.data;
    },
    onSuccess: (blob, investigationId) => {
      triggerDownload(
        blob,
        `multireporte-investigacion-${investigationId}.pdf`,
      );
      toast.success("Multireporte descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al descargar el multireporte",
      );
      toast.error(errorMessage);
    },
  });
};

/**
 * Download credit report
 */
export const useDownloadInvestigationCreditReport = () => {
  return useMutation({
    mutationFn: async (investigationId: string) => {
      const response = await downloadCreditReport(investigationId);
      return response.data;
    },
    onSuccess: (blob, investigationId) => {
      triggerDownload(blob, `reporte-credito-${investigationId}.pdf`);
      toast.success("Reporte de crédito descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al descargar el reporte de crédito",
      );
      toast.error(errorMessage);
    },
  });
};

/**
 * Download credit report metadata
 */
export const useDownloadCreditReportMetadata = () => {
  return useMutation({
    mutationFn: async (investigationId: string) => {
      const response = await downloadCreditReportMetadata(investigationId);
      return response.data;
    },
    onSuccess: (blob, investigationId) => {
      triggerDownload(blob, `metadata-${investigationId}.pdf`);
      toast.success("Metadata descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al descargar el metadata",
      );
      toast.error(errorMessage);
    },
  });
};

// ============================================================================
// SEND HOOKS - Sending reports via email
// ============================================================================

/**
 * Send investigation multireport via email
 */
export const useSendInvestigationMultireport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (investigationId: string) => sendMultireport(investigationId),
    onSuccess: (_, investigationId) => {
      toast.success("Multireporte enviado exitosamente");
      // Invalidate specific investigation (sent status may change)
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.detail(investigationId),
      });
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al enviar el multireporte",
      );
      toast.error(errorMessage);
    },
  });
};

/**
 * Send credit report via email
 */
export const useSendCreditReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (investigationId: string) => sendCreditReport(investigationId),
    onSuccess: (_, investigationId) => {
      toast.success("Reporte de crédito enviado exitosamente");
      // Invalidate specific investigation
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.detail(investigationId),
      });
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al enviar el reporte de crédito",
      );
      toast.error(errorMessage);
    },
  });
};

/**
 * Send metadata via email
 */
export const useSendMetadata = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (investigationId: string) => sendMetadata(investigationId),
    onSuccess: (_, investigationId) => {
      toast.success("Metadata enviado exitosamente");
      // Invalidate specific investigation
      queryClient.invalidateQueries({
        queryKey: queryKeys.investigations.detail(investigationId),
      });
    },
    onError: (error: any) => {
      const errorMessage = getApiErrorMessage(
        error,
        "Error al enviar el metadata",
      );
      toast.error(errorMessage);
    },
  });
};
